<script setup lang="ts">
import { CheckIcon, Loader2, Pencil, PlusCircle, Settings2, Trash2, X } from '@lucide/vue';
import { refDebounced } from '@vueuse/core';
import { ComboboxInput } from 'reka-ui';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { Badge } from '@/components/ui/badge/index';
import { Button } from '@/components/ui/button/index';
import {
    Combobox,
    ComboboxAnchor,
    ComboboxEmpty,
    ComboboxGroup,
    ComboboxItem,
    ComboboxItemIndicator,
    ComboboxList,
    ComboboxSeparator,
    ComboboxViewport,
} from '@/components/ui/combobox/index';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog/index';
import { Input } from '@/components/ui/input/index';
import { Label } from '@/components/ui/label/index';
import { ScrollArea } from '@/components/ui/scroll-area/index';
import { Separator } from '@/components/ui/separator/index';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet/index';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs/index';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip/index';

import { type CrudTagOption, useCrudTags } from '../composables/useCrudTags';

const TAG_COLORS = [
    '#94a3b8', '#f87171', '#fb923c', '#fbbf24', '#a3e635',
    '#34d399', '#22d3ee', '#60a5fa', '#a78bfa', '#f472b6',
] as const;

const props = withDefaults(defineProps<{
    model: string;
    modelValue: string[] | null | undefined;
    initialTags?: CrudTagOption[] | null;
    disabled?: boolean;
    allowManagement?: boolean;
    /** When provided, the component runs in "sheet-only" mode (Editor). Supports v-model:open from parent. */
    open?: boolean;
}>(), {
    initialTags: () => [],
    disabled: false,
    allowManagement: true,
    open: undefined,
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: string[]): void;
    (e: 'update:open', value: boolean): void;
}>();

const { t } = useI18n();
const search = ref('');
const searchDebounced = refDebounced(search, 250);
const manageOpenInternal = ref(false);

/**
 * When `open` prop is provided (Editor mode), the component runs in "sheet-only" mode:
 * no inline combobox is rendered, only the Sheet controlled by the parent.
 * When `open` is undefined (Creator mode), the inline combobox is shown and
 * the Sheet is controlled internally via the "manage" button.
 */
const isSheetOnly = computed(() => props.open !== undefined);

const manageOpen = computed({
    get: () => isSheetOnly.value ? props.open! : manageOpenInternal.value,
    set: (value: boolean) => {
        manageOpenInternal.value = value;
        emit('update:open', value);
    },
});
const sheetTab = ref<'attributed' | 'all'>('attributed');
const newTagColor = ref('#94a3b8');
const editDialogOpen = ref(false);
const editingTagId = ref<string | null>(null);
const editLabel = ref('');
const editColor = ref('#94a3b8');
const knownTags = ref<Record<string, CrudTagOption>>({});

const {
    suggestions,
    isLoading,
    isSaving,
    error,
    fetchSuggestions,
    createTag,
    updateTag,
    deleteTag,
} = useCrudTags(props.model);

/**
 * Local ref owns the selection state — avoids race conditions where the
 * Combobox reads a stale prop value during async operations.
 * We sync FROM the parent prop and TO the parent via emit.
 */
const localSelectedIds = ref<string[]>(Array.isArray(props.modelValue) ? [...props.modelValue] : []);

// Sync parent prop → local ref (when parent changes, e.g. after form save/reload)
watch(() => props.modelValue, (val) => {
    const incoming = Array.isArray(val) ? val : [];
    // Only update if genuinely different (avoid infinite loops)
    if (JSON.stringify(incoming) !== JSON.stringify(localSelectedIds.value)) {
        localSelectedIds.value = [...incoming];
    }
});

function updateSelectedIds(value: string[]): void {
    localSelectedIds.value = value;
    emit('update:modelValue', value);
}

const selectedTags = computed<CrudTagOption[]>(() => localSelectedIds.value.map((id) => knownTags.value[id] ?? {
    id,
    label: id,
    color: null,
}));

const sortedSuggestions = computed(() => [...suggestions.value].sort((a, b) => a.label.localeCompare(b.label)));

const normalizedSearch = computed(() => search.value.trim());

const canCreateFromSearch = computed(() => {
    if (!props.allowManagement) return false;
    if (normalizedSearch.value === '') return false;
    return !suggestions.value.some((tag) => tag.label.toLowerCase() === normalizedSearch.value.toLowerCase());
});

function mergeKnownTags(tags: CrudTagOption[]): void {
    for (const tag of tags) {
        knownTags.value[tag.id] = tag;
    }
}

function removeSelected(tagId: string): void {
    updateSelectedIds(localSelectedIds.value.filter((id) => id !== tagId));
}

function toggleTag(tagId: string): void {
    const set = new Set(localSelectedIds.value);
    if (set.has(tagId)) {
        set.delete(tagId);
    } else {
        set.add(tagId);
    }
    updateSelectedIds(Array.from(set));
}

function handleComboboxUpdate(values: string[]): void {
    updateSelectedIds(values);
}

/**
 * Handle Enter key on ComboboxInput.
 * Only prevent default + create when the search term is a genuinely new tag name.
 * Otherwise, let the event propagate normally.
 */
function onEnterKey(event: KeyboardEvent): void {
    // Always prevent default to stop form submission from Enter in an input
    event.preventDefault();
    if (canCreateFromSearch.value) {
        void handleCreateTagFromSearch();
    }
}

async function loadSuggestions(): Promise<void> {
    const tags = await fetchSuggestions(searchDebounced.value);
    mergeKnownTags(tags);
}

async function handleCreateTagFromSearch(): Promise<void> {
    const label = normalizedSearch.value;
    if (!label) return;
    const result = await createTag(label, newTagColor.value || null);
    if (!result) return;

    mergeKnownTags([{ id: result.id, label: result.label, color: result.color }]);
    updateSelectedIds([...localSelectedIds.value, result.id]);
    search.value = '';
    await loadSuggestions();
}

function startEdit(tag: CrudTagOption): void {
    editingTagId.value = tag.id;
    editLabel.value = tag.label;
    editColor.value = tag.color ?? '#94a3b8';
    editDialogOpen.value = true;
}

function cancelEdit(): void {
    editDialogOpen.value = false;
    editingTagId.value = null;
    editLabel.value = '';
    editColor.value = '#94a3b8';
}

async function handleUpdateTag(tagId: string): Promise<void> {
    const label = editLabel.value.trim();
    if (!label) return;

    const result = await updateTag(tagId, label, editColor.value || null);
    if (!result) return;

    mergeKnownTags([{ id: result.id, label: result.label, color: result.color }]);
    cancelEdit();
    await loadSuggestions();
}

async function handleDeleteTag(tagId: string): Promise<void> {
    const ok = await deleteTag(tagId);
    if (!ok) return;

    removeSelected(tagId);
    delete knownTags.value[tagId];
    await loadSuggestions();
}

onMounted(async () => {
    mergeKnownTags(props.initialTags ?? []);
    await loadSuggestions();
});

watch(() => props.initialTags, (value) => {
    mergeKnownTags(value ?? []);
}, { deep: true });

watch(searchDebounced, async () => {
    await loadSuggestions();
});
</script>

<template>
    <div class="space-y-2">
        <!--
            Inline combobox multi-select: only shown in Creator / inline contexts.
            Hidden in Editor ("sheet-only") mode to avoid two Combobox instances
            competing for the same selectedIds state.
        -->
        <template v-if="!isSheetOnly">
            <Combobox
                :model-value="localSelectedIds"
                multiple
                ignore-filter
                open-on-click
                :reset-search-term-on-select="false"
                @update:model-value="handleComboboxUpdate"
            >
                <ComboboxAnchor
                    class="flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]"
                >
                    <TransitionGroup
                        enter-active-class="transition-all duration-200 ease-out"
                        leave-active-class="transition-all duration-150 ease-in"
                        enter-from-class="opacity-0 scale-90"
                        enter-to-class="opacity-100 scale-100"
                        leave-from-class="opacity-100 scale-100"
                        leave-to-class="opacity-0 scale-90"
                    >
                        <Badge
                            v-for="tag in selectedTags"
                            :key="tag.id"
                            variant="secondary"
                            class="gap-1 pr-0.5 text-xs"
                        >
                            <span
                                class="size-2 shrink-0 rounded-full"
                                :style="{ backgroundColor: tag.color ?? '#94a3b8' }"
                            />
                            <span class="max-w-[120px] truncate">{{ tag.label }}</span>
                            <button
                                type="button"
                                class="ml-0.5 inline-flex size-4 items-center justify-center rounded-full transition-colors hover:bg-foreground/15"
                                :disabled="disabled"
                                @pointerdown.prevent
                                @click.stop="removeSelected(tag.id)"
                            >
                                <X class="size-2.5" />
                            </button>
                        </Badge>
                    </TransitionGroup>

                    <ComboboxInput
                        v-model="search"
                        :placeholder="selectedTags.length === 0 ? t('crud.tags.search_placeholder') : ''"
                        :disabled="disabled"
                        class="min-w-[80px] flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                        @keydown.enter="onEnterKey"
                    />

                    <Loader2 v-if="isLoading" class="size-4 shrink-0 animate-spin text-muted-foreground" />
                </ComboboxAnchor>

                <ComboboxList class="w-[--reka-combobox-trigger-width]">
                    <ComboboxViewport>
                        <ComboboxEmpty>
                            {{ isLoading ? t('crud.tags.loading') : t('crud.no_results') }}
                        </ComboboxEmpty>

                        <ComboboxGroup>
                            <ComboboxItem
                                v-for="tag in sortedSuggestions"
                                :key="tag.id"
                                :value="tag.id"
                            >
                                <span
                                    class="size-2.5 shrink-0 rounded-full"
                                    :style="{ backgroundColor: tag.color ?? '#94a3b8' }"
                                />
                                <span class="flex-1 truncate">{{ tag.label }}</span>
                                <span class="text-muted-foreground text-[11px] tabular-nums">
                                    {{ tag.usageCount ?? 0 }}
                                </span>
                                <ComboboxItemIndicator>
                                    <CheckIcon class="size-4" />
                                </ComboboxItemIndicator>
                            </ComboboxItem>
                        </ComboboxGroup>

                        <template v-if="canCreateFromSearch">
                            <ComboboxSeparator />
                            <ComboboxGroup>
                                <button
                                    type="button"
                                    class="flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden hover:bg-accent hover:text-accent-foreground"
                                    @click="handleCreateTagFromSearch"
                                >
                                    <PlusCircle class="size-4 text-muted-foreground" />
                                    {{ t('crud.tags.add') }}
                                    <span class="font-medium">"{{ normalizedSearch }}"</span>
                                </button>
                            </ComboboxGroup>
                        </template>
                    </ComboboxViewport>
                </ComboboxList>
            </Combobox>

            <!-- Manage tags button -->
            <div v-if="allowManagement" class="flex items-center justify-between">
                <p v-if="error" class="text-destructive text-sm">{{ error }}</p>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    class="ml-auto gap-1.5 text-xs text-muted-foreground"
                    :disabled="disabled"
                    @click="manageOpen = true"
                >
                    <Settings2 class="size-3.5" />
                    {{ t('crud.tags.title') }}
                </Button>
            </div>

            <p v-else-if="error" class="text-destructive text-sm">{{ error }}</p>
        </template>

        <!-- Management Sheet -->
        <Sheet v-model:open="manageOpen">
            <SheetContent class="flex flex-col gap-0 p-0 sm:max-w-md">
                <SheetHeader class="px-6 py-4">
                    <SheetTitle>{{ t('crud.tags.title') }}</SheetTitle>
                    <SheetDescription>{{ t('crud.tags.description') }}</SheetDescription>
                </SheetHeader>

                <Tabs v-model="sheetTab" class="flex flex-1 flex-col overflow-hidden">
                    <TabsList class="mx-6 mb-0 shrink-0">
                        <TabsTrigger value="attributed">
                            {{ t('crud.tags.attributed') }}
                            <Badge v-if="selectedTags.length > 0" variant="secondary" class="ml-1.5 px-1.5 text-[10px]">
                                {{ selectedTags.length }}
                            </Badge>
                        </TabsTrigger>
                        <TabsTrigger v-if="allowManagement" value="all">
                            {{ t('crud.tags.all') }}
                            <Badge v-if="sortedSuggestions.length > 0" variant="secondary" class="ml-1.5 px-1.5 text-[10px]">
                                {{ sortedSuggestions.length }}
                            </Badge>
                        </TabsTrigger>
                    </TabsList>

                    <!-- Attributed tab: current record's tags -->
                    <TabsContent value="attributed" class="mt-0 flex flex-1 flex-col overflow-hidden">
                        <!-- Combobox to search & add tags -->
                        <div class="space-y-3 border-b border-t px-6 py-4">
                            <Combobox
                                :model-value="localSelectedIds"
                                multiple
                                ignore-filter
                                open-on-click
                                :reset-search-term-on-select="false"
                                @update:model-value="handleComboboxUpdate"
                            >
                                <ComboboxAnchor
                                    class="flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]"
                                >
                                    <ComboboxInput
                                        v-model="search"
                                        :placeholder="t('crud.tags.search_placeholder')"
                                        :disabled="disabled"
                                        class="min-w-[80px] flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                                        @keydown.enter="onEnterKey"
                                    />
                                    <Loader2 v-if="isLoading" class="size-4 shrink-0 animate-spin text-muted-foreground" />
                                </ComboboxAnchor>

                                <ComboboxList class="w-[--reka-combobox-trigger-width]">
                                    <ComboboxViewport>
                                        <ComboboxEmpty>
                                            {{ isLoading ? t('crud.tags.loading') : t('crud.no_results') }}
                                        </ComboboxEmpty>

                                        <ComboboxGroup>
                                            <ComboboxItem
                                                v-for="tag in sortedSuggestions"
                                                :key="tag.id"
                                                :value="tag.id"
                                            >
                                                <span
                                                    class="size-2.5 shrink-0 rounded-full"
                                                    :style="{ backgroundColor: tag.color ?? '#94a3b8' }"
                                                />
                                                <span class="flex-1 truncate">{{ tag.label }}</span>
                                                <ComboboxItemIndicator>
                                                    <CheckIcon class="size-4" />
                                                </ComboboxItemIndicator>
                                            </ComboboxItem>
                                        </ComboboxGroup>

                                        <template v-if="canCreateFromSearch">
                                            <ComboboxSeparator />
                                            <ComboboxGroup>
                                                <button
                                                    type="button"
                                                    class="flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden hover:bg-accent hover:text-accent-foreground"
                                                    @click="handleCreateTagFromSearch"
                                                >
                                                    <PlusCircle class="size-4 text-muted-foreground" />
                                                    {{ t('crud.tags.add') }}
                                                    <span class="font-medium">"{{ normalizedSearch }}"</span>
                                                </button>
                                            </ComboboxGroup>
                                        </template>
                                    </ComboboxViewport>
                                </ComboboxList>
                            </Combobox>

                            <!-- Color swatches for new tag creation -->
                            <div v-if="allowManagement" class="flex items-center gap-1.5">
                                <span class="mr-1 text-muted-foreground text-xs">{{ t('crud.tags.color') }}</span>
                                <button
                                    v-for="color in TAG_COLORS"
                                    :key="color"
                                    type="button"
                                    class="flex size-6 items-center justify-center rounded-full border-2 transition-transform hover:scale-110"
                                    :class="newTagColor === color ? 'border-foreground scale-110' : 'border-transparent'"
                                    @click="newTagColor = color"
                                >
                                    <span class="size-4 rounded-full" :style="{ backgroundColor: color }" />
                                </button>
                            </div>
                        </div>

                        <!-- Attributed tags list -->
                        <ScrollArea class="flex-1">
                            <div class="divide-y">
                                <div
                                    v-for="tag in selectedTags"
                                    :key="tag.id"
                                    class="group flex items-center gap-3 px-6 py-2.5 transition-colors hover:bg-muted/40"
                                >
                                    <span
                                        class="size-3 shrink-0 rounded-full"
                                        :style="{ backgroundColor: tag.color ?? '#94a3b8' }"
                                    />
                                    <span class="flex-1 truncate text-sm">{{ tag.label }}</span>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon-sm"
                                        class="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                                        :disabled="disabled"
                                        @click="removeSelected(tag.id)"
                                    >
                                        <X class="size-3.5" />
                                    </Button>
                                </div>

                                <div v-if="selectedTags.length === 0" class="px-6 py-8 text-center">
                                    <p class="text-muted-foreground text-sm">{{ t('crud.tags.none_selected') }}</p>
                                </div>
                            </div>
                        </ScrollArea>
                    </TabsContent>

                    <!-- All tab: every tag with management actions -->
                    <TabsContent v-if="allowManagement" value="all" class="mt-0 flex flex-1 flex-col overflow-hidden">
                        <ScrollArea class="flex-1 border-t">
                            <div class="divide-y">
                                <div
                                    v-for="tag in sortedSuggestions"
                                    :key="tag.id"
                                    class="group flex items-center gap-3 px-6 py-2.5 transition-colors hover:bg-muted/40"
                                >
                                    <button
                                        type="button"
                                        class="flex size-5 items-center justify-center rounded border transition-colors"
                                        :class="localSelectedIds.includes(tag.id) ? 'border-primary bg-primary text-primary-foreground' : 'border-input hover:border-primary/50'"
                                        :disabled="disabled"
                                        @click="toggleTag(tag.id)"
                                    >
                                        <CheckIcon v-if="localSelectedIds.includes(tag.id)" class="size-3" />
                                    </button>
                                    <span
                                        class="size-3 shrink-0 rounded-full"
                                        :style="{ backgroundColor: tag.color ?? '#94a3b8' }"
                                    />
                                    <span class="flex-1 truncate text-sm">{{ tag.label }}</span>
                                    <Badge variant="outline" class="text-[11px] font-normal tabular-nums">
                                        {{ t('crud.tags.used_count', { count: tag.usageCount ?? 0 }) }}
                                    </Badge>
                                    <div class="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon-sm"
                                            :disabled="disabled || isSaving"
                                            @click="startEdit(tag)"
                                        >
                                            <Pencil class="size-3.5" />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon-sm"
                                            class="text-destructive hover:text-destructive"
                                            :disabled="disabled || isSaving"
                                            @click="handleDeleteTag(tag.id)"
                                        >
                                            <Trash2 class="size-3.5" />
                                        </Button>
                                    </div>
                                </div>

                                <div v-if="sortedSuggestions.length === 0" class="px-6 py-8 text-center">
                                    <p class="text-muted-foreground text-sm">{{ t('crud.no_results') }}</p>
                                </div>
                            </div>
                        </ScrollArea>
                    </TabsContent>
                </Tabs>

                <p v-if="error" class="border-t px-6 py-3 text-destructive text-sm">{{ error }}</p>
            </SheetContent>
        </Sheet>

        <!-- Edit Dialog -->
        <Dialog v-model:open="editDialogOpen">
            <DialogContent class="sm:max-w-sm">
                <DialogHeader>
                    <DialogTitle>{{ t('crud.tags.edit') }}</DialogTitle>
                    <DialogDescription>{{ t('crud.tags.description') }}</DialogDescription>
                </DialogHeader>

                <div class="space-y-4">
                    <div class="space-y-2">
                        <Label>{{ t('crud.tags.label_placeholder') }}</Label>
                        <Input v-model="editLabel" :placeholder="t('crud.tags.label_placeholder')" />
                    </div>

                    <div class="space-y-2">
                        <Label>{{ t('crud.tags.color') }}</Label>
                        <div class="flex items-center gap-1.5">
                            <TooltipProvider :delay-duration="300">
                                <Tooltip v-for="color in TAG_COLORS" :key="color">
                                    <TooltipTrigger as-child>
                                        <button
                                            type="button"
                                            class="flex size-7 items-center justify-center rounded-full border-2 transition-transform hover:scale-110"
                                            :class="editColor === color ? 'border-foreground scale-110' : 'border-transparent'"
                                            @click="editColor = color"
                                        >
                                            <span class="size-5 rounded-full" :style="{ backgroundColor: color }" />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" class="text-xs">
                                        {{ color }}
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    </div>

                    <Separator />

                    <div class="flex items-center gap-2">
                        <Label class="text-muted-foreground text-xs">{{ t('crud.tags.color') }}</Label>
                        <Input v-model="editColor" type="color" class="h-8 w-12 cursor-pointer p-0.5" />
                        <span class="font-mono text-muted-foreground text-xs">{{ editColor }}</span>
                    </div>
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" size="sm" :disabled="isSaving" @click="cancelEdit">
                        {{ t('crud.tags.cancel') }}
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        :disabled="isSaving || !editingTagId || !editLabel.trim()"
                        @click="editingTagId && handleUpdateTag(editingTagId)"
                    >
                        {{ t('crud.tags.save') }}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </div>
</template>
